<!-- ClientPortalDrawer.vue -->
<template>
  <div class="client-portal-drawer">
      <!-- Overlay -->
  <div
    v-if="value"
    class="drawer-overlay"
    @click="$emit('input', false)"
  />

  <!-- Drawer -->
  <transition name="drawer">
    <div
      v-if="value"
      class="drawer-container"
    >
      <!-- Drawer Header -->
      <div class="drawer-header">
        <div class="drawer-title-section">
          <Title size="5">Client Portal Management</Title>
          <Subtitle v-if="entityName">
            {{ entityName }}
          </Subtitle>
        </div>
        <Button
          @click="$emit('input', false)"
          variant="ghost"
          size="2"
          iconOnly="fas fa-times"
          class="drawer-close-btn"
        />
      </div>

      <!-- Portal Status Banner -->
      <div class="portal-status-banner">
        <div class="status-content">
          <div class="status-icon">
            <i class="fas fa-user-circle"></i>
          </div>
          <div class="status-text">
            <div class="status-title">Portal Status</div>
            <div class="status-description">
              {{ portalStatusDescription }}
            </div>
          </div>
          <Badge :status="portalStatusBadge.status" class="status-badge">
            {{ portalStatusBadge.text }}
          </Badge>
        </div>
      </div>

      <!-- Drawer Content -->
       <!-- IMP: Only show invite links and edit checklist part if the advisor has edit access to the entity -->
      <div v-if="editEntity" class="drawer-content"> 
        <!-- Invitation Section -->
        <div class="drawer-section invitation-section">
          <div class="section-header">
            <Title size="5" class="section-title">
              <i class="fas fa-envelope section-icon"></i>
              Client Portal Access
            </Title>
          </div>
          <div class="section-content">
            <EntityInviteForm
              :entity-external-id="entityExternalId"
              :advisor-slug="advisorSlug"
              :entity-email="entityEmail"
              @invitation-link-generated="handleInvitationLinkGenerated"
            />
          </div>
        </div>

        <!-- Divider -->
        <div class="drawer-divider"></div>

        <!-- Document Checklist Section -->
        <div class="drawer-section checklist-section">
          <div class="section-header">
            <Title size="5" class="section-title">
              <i class="fas fa-file-alt section-icon"></i>
              Document Checklist Management
            </Title>
          </div>
          <div class="section-content">
            <DocumentChecklist2
              :entity-external-id="entityExternalId"
              :entity-name="entityName"
            />
          </div>
        </div>
      </div>
    </div>
  </transition>
  </div>
</template>

<script>
import {
  Badge,
  Button,
  Subtitle,
  Title,
} from '@stakeui';
import EntityInviteForm from './EntityInviteForm.vue';
import DocumentChecklist2 from './DocumentChecklist2.vue';

export default {
  name: 'ClientPortalDrawer',
  components: {
    Badge,
    Button,
    Subtitle,
    Title,
    EntityInviteForm,
    DocumentChecklist2,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    entityExternalId: {
      type: String,
      required: true,
    },
    advisorSlug: {
      type: String,
      required: true,
    },
    entityName: {
      type: String,
      default: '',
    },
    entityEmail: {
      type: String,
      default: '',
    },
    portalStatus: {
      type: String,
      default: 'not_invited',
    },
    editEntity:{
      type: Boolean,
      default: false,
    }
  },
  emits: ['input', 'invitation-link-generated'],
  data() {
    return {
      // No tab state needed anymore
    };
  },
  computed: {
    portalStatusBadge() {
      switch (this.portalStatus) {
        case 'not_invited':
          return {
            status: 'warning',
            text: 'Not Invited'
          };
        case 'invited_pending':
          return {
            status: 'warning',
            text: 'Pending Activation'
          };
        case 'activated':
          return {
            status: 'success',
            text: 'Active'
          };
        default:
          return {
            status: 'warning',
            text: 'Not Invited'
          };
      }
    },
    portalStatusDescription() {
      if (!this.entityName) {
        return 'Loading portal status...';
      }
      
      switch (this.portalStatus) {
        case 'activated':
          return `${this.entityName} has access to the client portal.`;
        case 'invited_pending':
          return `${this.entityName} has been invited but hasn't completed setup yet.`;
        default:
          return `${this.entityName} hasn't been invited to the client portal yet.`;
      }
    },
  },
  methods: {
    handleInvitationLinkGenerated(data) {
      this.$emit('invitation-link-generated', data);
    },
  },
};
</script>

<style scoped>
.drawer-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  backdrop-filter: blur(2px);
}

.drawer-container {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 600px;
  max-width: 90vw;
  background: white;
  z-index: 1001;
  display: flex;
  flex-direction: column;
  box-shadow: -4px 0 24px rgba(0, 0, 0, 0.15);
}

.drawer-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 1.5rem 2rem;
  border-bottom: 1px solid #e2e8f0;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
}

.drawer-title-section {
  flex: 1;
}

.drawer-close-btn {
  flex-shrink: 0;
  margin-left: 1rem;
}

.portal-status-banner {
  padding: 1rem 2rem;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}

.status-content {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.status-icon {
  color: #64748b;
  font-size: 1.5rem;
}

.status-text {
  flex: 1;
}

.status-title {
  font-weight: 600;
  color: #334155;
  font-size: 0.875rem;
}

.status-description {
  color: #64748b;
  font-size: 0.8125rem;
  margin-top: 0.25rem;
}

.status-badge {
  flex-shrink: 0;
}

.drawer-content {
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

.drawer-section {
  display: flex;
  flex-direction: column;
}

.invitation-section {
  flex-shrink: 0;
  padding: 2rem 2rem 1.5rem;
}

.checklist-section {
  flex: 1;
  padding: 1.5rem 2rem 2rem;
  min-height: 0;
}

.section-header {
  margin-bottom: 1.5rem;
}

.section-title {
  display: flex;
  align-items: center;
  color: #334155;
  margin: 0;
}

.section-icon {
  margin-right: 0.75rem;
  color: #64748b;
  font-size: 1rem;
}

.section-content {
  flex: 1;
  min-height: 0;
}

.drawer-divider {
  height: 1px;
  background: linear-gradient(to right, transparent, #e2e8f0 20%, #e2e8f0 80%, transparent);
  margin: 0 2rem;
}

/* Drawer transition */
.drawer-enter-active,
.drawer-leave-active {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.drawer-enter-from,
.drawer-leave-to {
  transform: translateX(100%);
}

.drawer-enter-to,
.drawer-leave-from {
  transform: translateX(0);
}

/* Tab content fade animation */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .drawer-container {
    width: 100vw;
    max-width: 100vw;
  }
  
  .drawer-header,
  .portal-status-banner,
  .drawer-tabs,
  .drawer-content {
    padding-left: 1rem;
    padding-right: 1rem;
  }
}

/* Ensure DocumentChecklist2 fits nicely in drawer */
:deep(.document-checklist-container) {
  padding: 0;
  margin: 0;
  box-shadow: none;
  border-radius: 0;
  background: transparent;
}

:deep(.document-checklist-container .header) {
  margin-bottom: 1.5rem;
}

/* Adjust DocumentChecklist2 sections for drawer */
:deep(.checklist-content) {
  background-color: transparent;
  border: none;
  padding: 0;
}

:deep(.footer-actions) {
  background-color: #f8fafc;
  margin-top: 1.5rem;
}
</style> 